import React, { useMemo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  find,
  keyBy,
  mapValues,
  merge,
  orderBy,
  pickBy,
  uniq,
} from 'lodash-es';
import { useQuery } from '../../hooks/use-query';
import { useFields } from '../../hooks/use-fields';

const toSymbol = (name) =>
  ({ lime: 'L', phosphorus: 'P', potassium: 'K' })[name];

const toName = (symbol) =>
  ({ L: 'lime', P: 'phosphorus', K: 'potassium' })[symbol];

const Context = React.createContext();

export const useContext = () => React.useContext(Context);

Provider.propTypes = {
  children: PropTypes.node,
};

export function Provider({ children }) {
  const queryParams = useQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState();
  const [allInstances, setAllInstances] = useState([]);
  const [selectedInstances, setSelectedInstances] = useState([]);
  const [focusedInstanceId, innerSetFocusedInstanceId] = useState();
  const [tab, setTab] = useState(queryParams.get('tab') || 'L');
  const { allFields: growerFields, selectedAaids, focusedAaid } = useFields();
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [isSidePanelVisible, setSidePanelVisible] = useState(false);

  const years = useMemo(
    () =>
      uniq([new Date().getFullYear(), ...allInstances.map((i) => i.cropYear)])
        .sort()
        .reverse(),
    [allInstances],
  );

  const [year, setYear] = useState(
    Number(queryParams.get('year') || new Date().getFullYear()),
  );

  useEffect(() => {
    if (!years.includes(year)) setYear(new Date().getFullYear());
  }, [year, years]);

  const [sortModel, setSortModel] = useState([
    { field: 'fieldName', sort: 'asc' },
  ]);

  const toggleSidePanel = useCallback(() => {
    setSidePanelVisible(!isSidePanelVisible);
  }, [isSidePanelVisible, setSidePanelVisible]);

  const setFocusedInstanceId = useCallback(
    (id) =>
      innerSetFocusedInstanceId((current) => (current === id ? null : id)),
    [],
  );

  const updateInstance = useCallback((updatedRx) => {
    const compositeId = (rx) => [rx.id, rx.cropYear].join();
    const updatedRxs = keyBy([updatedRx].flat(), compositeId);

    setAllInstances((current) => {
      const existingIds = new Set(current.map(compositeId));
      const newRxs = Object.values(updatedRxs).filter(
        (rx) => !existingIds.has(compositeId(rx)),
      );

      return [
        ...current.map((rx) => updatedRxs[compositeId(rx)] || rx),
        ...newRxs,
      ];
    });
  }, []);

  const removeInstance = useCallback((id) => {
    const ids = [id].flat();

    setAllInstances((current) =>
      current.filter((instance) => !ids.includes(instance.id)),
    );
  }, []);

  const instances = useMemo(
    () => allInstances.filter((rx) => rx.cropYear === year),
    [allInstances, year],
  );

  const getSelectedInstances = useCallback(
    () => allInstances.filter((i) => selectedInstances.includes(i.id)),
    [allInstances, selectedInstances],
  );

  const getAllPrescriptionsForField = useCallback(
    (id, aaid) =>
      allInstances
        .filter((i) => i.id === id)
        .flatMap((i) => i.fields.filter((f) => f.aaid === aaid))
        .reduce(
          (memo, f) =>
            merge(memo, {
              lime: f.lime,
              phosphorus: f.phosphorus,
              potassium: f.potassium,
            }),
          {},
        ),
    [allInstances],
  );

  const getSelectedPrescriptions = useCallback(
    (filters) => {
      const selected =
        tab === 'instances'
          ? selectPrescriptionsFrom(instances, {
              instanceFilter: ({ id }) => selectedInstances.includes(id),
              fieldFilter: () => true,
              prescriptionFilter: (rx) => rx?.status === 'COMPLETED',
            })
          : selectPrescriptionsFrom(instances, {
              instanceFilter: (i) => i.isApplied,
              fieldFilter: (f) => selectedAaids.includes(f.aaid),
              prescriptionFilter: (rx, current) =>
                rx && !find(current, { aaid: rx.aaid }),
            });

      return filters
        ? pickBy(selected, (_, key) => filters.includes(toSymbol(key)))
        : selected;
    },
    [instances, tab, selectedAaids, selectedInstances],
  );

  const getSelectedPrescriptionIds = useCallback(
    (filters) => {
      const prescriptions = getSelectedPrescriptions(filters);
      return mapValues(prescriptions, (value) => value.map((v) => v.id));
    },
    [getSelectedPrescriptions],
  );

  const getInstancesForField = useCallback(
    (field, symbol) => {
      const name = toName(symbol);

      return orderBy(instances, ['createdAt'])
        .map((i) => {
          const inInstance = i.fields.find((f) => f.aaid === field.aaid);
          return { ...i, [name]: inInstance?.[name] };
        })
        .filter((i) => i[name]);
    },
    [instances],
  );

  const getFields = useCallback(
    (symbol) =>
      growerFields.map((field) => {
        const name = toName(symbol);
        const [prescription] = orderBy(instances, ['createdAt'], ['desc'])
          .filter((i) => i.isApplied)
          .map((i) => {
            const rxField = i.fields.find((f) => f.aaid === field.aaid);
            return rxField?.[name];
          })
          .filter(Boolean);

        return {
          ...field,
          fullFieldAdjustment: prescription?.fullFieldAdjustment,
          prescription,
        };
      }),
    [growerFields, instances],
  );

  const arePrescriptionsSelected = useMemo(
    () => Object.values(getSelectedPrescriptionIds()).some((ids) => ids.length),
    [getSelectedPrescriptionIds],
  );

  const state = useMemo(
    () => ({
      isLoading,
      setLoading,

      tab,
      setTab,

      sortModel,
      setSortModel,

      filterModel,
      setFilterModel,

      years,
      year,
      setYear,

      getFields,

      setInstances: setAllInstances,
      instances,
      updateInstance,
      removeInstance,
      getSelectedInstances,
      setSelectedInstances,
      selectedInstances,
      focusedInstanceId,
      setFocusedInstanceId,
      getInstancesForField,

      getAllPrescriptionsForField,
      getSelectedPrescriptions,
      getSelectedPrescriptionIds,
      arePrescriptionsSelected,

      isSidePanelVisible,
      toggleSidePanel,
    }),
    [
      isLoading,
      tab,
      sortModel,
      filterModel,
      years,
      year,
      getFields,
      instances,
      updateInstance,
      removeInstance,
      getSelectedInstances,
      selectedInstances,
      focusedInstanceId,
      setFocusedInstanceId,
      getInstancesForField,
      getAllPrescriptionsForField,
      getSelectedPrescriptions,
      getSelectedPrescriptionIds,
      arePrescriptionsSelected,
      isSidePanelVisible,
      toggleSidePanel,
    ],
  );

  useEffect(() => {
    navigate(`${location.pathname}?tab=${tab}&year=${year}`, { replace: true });
  }, [navigate, location.pathname, tab, year]);

  useEffect(() => {
    if (!focusedInstanceId || !focusedAaid) return;

    const instance = allInstances.find((i) => i.id === focusedInstanceId);
    if (!instance) {
      setFocusedInstanceId(null);
      return;
    }

    const { fields } = instance;
    const instanceAaids = fields.map((f) => f.aaid);
    if (!instanceAaids.includes(focusedAaid)) setFocusedInstanceId(null);
  }, [allInstances, focusedAaid, focusedInstanceId, setFocusedInstanceId]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
}

function selectPrescriptionsFrom(
  instances,
  { instanceFilter, fieldFilter, prescriptionFilter },
) {
  const getInstances = () =>
    orderBy(instances, ['cropYear', 'createdAt'], ['desc', 'desc']).filter(
      instanceFilter,
    );

  const getFields = (instance) => instance.fields.filter(fieldFilter);

  const getPrescriptions = (nutrient, currentlySelected, field) =>
    [field[nutrient]]
      .flat()
      .filter((rx) => prescriptionFilter(rx, currentlySelected[nutrient]))
      .map((rx) => ({ fieldName: field.name, ...rx }));

  return getInstances().reduce(
    (selected, instance) => {
      Object.keys(selected).forEach((nutrient) => {
        selected[nutrient].push(
          ...getFields(instance).flatMap(
            getPrescriptions.bind(null, nutrient, selected),
          ),
        );
      });

      return selected;
    },
    { lime: [], phosphorus: [], potassium: [] },
  );
}
